type Primitive = string | number | boolean | null | undefined;

export const ATHLETE_REFERRAL_COOKIE = "athlete_referral_meta";
export const ATHLETE_REFERRAL_FUNNEL = "athlete_referral_conversion";
export const ATHLETE_ONBOARDING_FUNNEL = "athlete_onboarding_conversion";

export const ATHLETE_REFERRAL_EVENTS = {
  LINK_CLICKED: "athlete_referral_link_clicked",
  LANDING_VIEWED: "athlete_referral_landing_viewed",
  CONVERSION_COMPLETED: "athlete_referral_conversion_completed",
} as const;

export const ATHLETE_ONBOARDING_EVENTS = {
  ACCOUNT_CREATED: "athlete_onboarding_account_created",
  AGREEMENT_SIGNED: "athlete_onboarding_agreement_signed",
  SOCIAL_ADDED: "athlete_onboarding_social_added",
  COMPLETED: "athlete_onboarding_completed",
} as const;

export type AthleteReferralMeta = {
  code: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
  capturedAt?: string;
};

export function sanitizePromoCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return normalized.length ? normalized : null;
}

export function sanitizeAttributionValue(
  value: string | null | undefined,
  fallback: string,
  maxLength = 64,
): string {
  if (!value) return fallback;
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
  if (!normalized) return fallback;
  return normalized.slice(0, maxLength);
}

export function getCookieValue(
  cookieHeader: string | null | undefined,
  name: string,
): string | null {
  if (!cookieHeader) return null;

  const cookies = cookieHeader.split(";");
  for (const part of cookies) {
    const [rawName, ...rawValue] = part.trim().split("=");
    if (rawName !== name) continue;
    const value = rawValue.join("=");
    if (!value) return null;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  return null;
}

export function parseAthleteReferralMeta(
  raw: string | null | undefined,
): AthleteReferralMeta | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AthleteReferralMeta>;
    const code = sanitizePromoCode(parsed.code);
    if (!code) return null;

    return {
      code,
      source: sanitizeAttributionValue(parsed.source, "athlete"),
      medium: sanitizeAttributionValue(parsed.medium, "referral"),
      campaign: sanitizeAttributionValue(parsed.campaign, "nil"),
      content: sanitizeAttributionValue(parsed.content, "profile_link"),
      capturedAt:
        typeof parsed.capturedAt === "string" ? parsed.capturedAt : undefined,
    };
  } catch {
    return null;
  }
}

export function isReferralMetaForPromoCode(
  meta: AthleteReferralMeta | null,
  promoCode: string | null | undefined,
): meta is AthleteReferralMeta {
  const normalizedPromoCode = sanitizePromoCode(promoCode);
  return Boolean(meta && normalizedPromoCode && meta.code === normalizedPromoCode);
}

/**
 * Resolve the referral promo code that should be attributed to a signup.
 * Prefers an explicit `?promo=` URL value, then falls back to the persisted
 * `athlete_referral_meta` cookie set when the referral short-link was clicked.
 * Pure: callers pass the raw URL value and cookie header (e.g. document.cookie).
 */
export function resolveReferralPromoCode(input: {
  urlPromo?: string | null;
  cookieHeader?: string | null;
}): string | null {
  const fromUrl = sanitizePromoCode(input.urlPromo);
  if (fromUrl) return fromUrl;

  const meta = parseAthleteReferralMeta(
    getCookieValue(input.cookieHeader, ATHLETE_REFERRAL_COOKIE),
  );
  return meta?.code ?? null;
}

export function buildAthleteReferralProperties(
  meta: AthleteReferralMeta | null,
  extras: Record<string, Primitive> = {},
): Record<string, Primitive> {
  return {
    funnel: ATHLETE_REFERRAL_FUNNEL,
    promo_code: meta?.code,
    source: meta?.source,
    medium: meta?.medium,
    campaign: meta?.campaign,
    content: meta?.content,
    ...extras,
  };
}

export function buildAthleteOnboardingProperties(
  extras: Record<string, Primitive> = {},
): Record<string, Primitive> {
  return {
    funnel: ATHLETE_ONBOARDING_FUNNEL,
    ...extras,
  };
}

export function hasAnyAthleteSocialProfile(
  athlete:
    | {
        facebookUrl?: string | null;
        instagramUrl?: string | null;
        twitterUrl?: string | null;
        tiktokUrl?: string | null;
        youtubeUrl?: string | null;
      }
    | null
    | undefined,
): boolean {
  return Boolean(
    athlete?.facebookUrl ||
      athlete?.instagramUrl ||
      athlete?.twitterUrl ||
      athlete?.tiktokUrl ||
      athlete?.youtubeUrl,
  );
}
